import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { roomService } from '../../../services/api';

const ReservationFilters = ({ onFiltersChange, onClearFilters }) => {
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    paymentStatus: '',
    roomType: '',
    guestSearch: ''
  });

  const [roomTypeOptions, setRoomTypeOptions] = useState([
    { value: '', label: 'Todos os Tipos' }
  ]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Carregar tipos de quarto da API
  useEffect(() => {
    const loadRoomTypes = async () => {
      try {
        setLoadingTypes(true);
        const response = await roomService.getRoomTypes();
        
        // A API retorna { types: [...], enum: [...] }
        const typesArray = response.types || response;
        
        // Se já estiver no formato correto, usar diretamente
        const typesOptions = Array.isArray(typesArray) 
          ? typesArray.map(type => ({
              value: type.value || type.name,
              label: type.label || type.name
            }))
          : [];
        
        setRoomTypeOptions([
          { value: '', label: 'Todos os Tipos' },
          ...typesOptions
        ]);
      } catch (error) {
        console.error('Erro ao carregar tipos de quarto:', error);
        // Em caso de erro, manter os tipos padrão como fallback
        setRoomTypeOptions([
          { value: '', label: 'Todos os Tipos' },
          { value: 'STANDARD', label: 'Standard' },
          { value: 'DELUXE', label: 'Deluxe' },
          { value: 'SUITE', label: 'Suíte' },
          { value: 'PRESIDENTIAL', label: 'Presidencial' }
        ]);
      } finally {
        setLoadingTypes(false);
      }
    };

    loadRoomTypes();
  }, []);

  const paymentStatusOptions = [
    { value: '', label: 'Todos os Status' },
    { value: 'paid', label: 'Pago' },
    { value: 'pending', label: 'Pendente' },
    { value: 'overdue', label: 'Em Atraso' },
    { value: 'cancelled', label: 'Cancelado' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleDateRangeChange = (type, value) => {
    const newDateRange = { ...filters.dateRange, [type]: value };
    const newFilters = { ...filters, dateRange: newDateRange };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      dateRange: { start: '', end: '' },
      paymentStatus: '',
      roomType: '',
      guestSearch: ''
    };
    setFilters(clearedFilters);
    onClearFilters();
  };

  const hasActiveFilters = filters.dateRange.start || filters.dateRange.end || 
                          filters.paymentStatus || filters.roomType || filters.guestSearch;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Filtros</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            iconName="X"
            iconPosition="left"
          >
            Limpar Filtros
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Date Range */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Período</label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              placeholder="Data inicial"
              value={filters.dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="text-sm"
            />
            <Input
              type="date"
              placeholder="Data final"
              value={filters.dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="text-sm"
            />
          </div>
        </div>

        {/* Payment Status */}
        <div>
          <Select
            label="Status do Pagamento"
            options={paymentStatusOptions}
            value={filters.paymentStatus}
            onChange={(value) => handleFilterChange('paymentStatus', value)}
          />
        </div>

        {/* Room Type */}
        <div>
          <Select
            label={`Tipo de Quarto${loadingTypes ? ' (carregando...)' : ''}`}
            options={roomTypeOptions}
            value={filters.roomType}
            onChange={(value) => handleFilterChange('roomType', value)}
            disabled={loadingTypes}
          />
        </div>

        {/* Guest Search */}
        <div>
          <Input
            label="Buscar Hóspede"
            type="search"
            placeholder="Nome ou documento"
            value={filters.guestSearch}
            onChange={(e) => handleFilterChange('guestSearch', e.target.value)}
          />
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
        <span className="text-sm text-muted-foreground">Filtros rápidos:</span>
        <Button
          variant="outline"
          size="xs"
          onClick={() => handleFilterChange('paymentStatus', 'pending')}
        >
          Pagamentos Pendentes
        </Button>
        <Button
          variant="outline"
          size="xs"
          onClick={() => {
            const today = new Date().toISOString().split('T')[0];
            handleDateRangeChange('start', today);
            handleDateRangeChange('end', today);
          }}
        >
          Check-ins Hoje
        </Button>
        <Button
          variant="outline"
          size="xs"
          onClick={() => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = tomorrow.toISOString().split('T')[0];
            handleDateRangeChange('start', tomorrowStr);
            handleDateRangeChange('end', tomorrowStr);
          }}
        >
          Check-outs Amanhã
        </Button>
      </div>
    </div>
  );
};

export default ReservationFilters;